import { useEffect, useRef } from 'react';

import { getDesignationColor } from '@/services/public-land-colors';
import { formatAcres } from '@/utils/number-format';

interface PublicLandInfoCardProps {
  designation: string;
  name: string;
  manager: string;
  acres: number | string;
  onDismiss: () => void;
}

export function PublicLandInfoCard({
  designation,
  name,
  manager,
  acres,
  onDismiss,
}: PublicLandInfoCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const swatchColor = getDesignationColor(designation);

  // Dismiss on tap outside the card
  useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      // CAST: PointerEvent.target is EventTarget; contains() requires Node — EventTarget is always a Node in DOM context
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        onDismiss();
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [onDismiss]);

  return (
    <div
      ref={cardRef}
      data-testid="public-land-info-card"
      style={{ zIndex: 1100 }}
      className="absolute bottom-0 left-0 right-0 animate-slide-up"
      role="dialog"
      aria-label={`Public land details: ${name}`}
    >
      <div className="bg-bark-800 rounded-t-2xl border-t-[0.5px] border-bark-600 shadow-card-md px-4 pt-3 pb-6 max-h-[40vh] overflow-y-auto">
        {/* Drag handle */}
        <div className="w-10 h-1 rounded-full bg-bark-600 mx-auto mb-3" aria-hidden="true" />

        {/* Header row */}
        <div className="flex items-start gap-3 mb-3">
          {/* Designation color swatch */}
          <div
            data-testid="designation-swatch"
            style={{ backgroundColor: swatchColor, flexShrink: 0 }}
            className="w-9 h-9 rounded-full border-2 border-white/20 shadow-md"
            aria-hidden="true"
          />
          {/* Text stack */}
          <div className="min-w-0">
            <p className="field-label leading-tight mb-0.5">
              PUBLIC LAND
            </p>
            <p className="font-serif text-[20px] leading-tight text-bark-50 truncate">
              {name}
            </p>
          </div>
        </div>

        {/* Details */}
        <div className="ml-12 space-y-1">
          <p className="text-body-sm text-bark-300">
            <span className="text-bark-400">Type: </span>{designation}
          </p>
          <p className="text-body-sm text-bark-300">
            <span className="text-bark-400">Manager: </span>{manager}
          </p>
          <p className="text-body-sm text-bark-300">
            <span className="text-bark-400">Size: </span>{formatAcres(acres)}
          </p>
        </div>
      </div>
    </div>
  );
}
